import { useState, useEffect, useCallback } from 'react';
import EntryDataServices from '../../../services/EntryDataServices';
import { organizeCitiesByState, createInitialRows, processSpecialtyData, populateRowsWithData } from '../utils/entryDataUtils';

/**
 * Custom hook for managing cities and specialties data
 * @param {string} initialHeader - The initial header for the data
 * @returns {Object} - Contains cities, specialties, loading state, and error handling
 */
export const useCitiesAndSpecialties = (initialHeader) => {
  const [completeCities, setCompleteCities] = useState([]);
  const [completeSpecialties, setCompleteSpecialties] = useState([]);
  const [cities, setCities] = useState([]);
  const [stateGroupings, setStateGroupings] = useState([]);
  const [rowsExistent, setRowsExistent] = useState([]);
  const [fixedNetworkAdjustmentFactors, setFixedNetworkAdjustmentFactors] = useState([]);
  const [listOfHints, setListOfHints] = useState([]);
  const [separationLines, setSeparationLines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const populateRows = useCallback((rows, cityNames, data, hints, separationLinesList) => {
    if (!data || !Array.isArray(data)) {
      console.warn('populateRows: data is not an array or is undefined');
      return;
    }

    populateRowsWithData(rows, cityNames, data);
    
    if (hints && Array.isArray(hints)) {
      setListOfHints(hints);
    }

    if (separationLinesList && Array.isArray(separationLinesList)) {
      setSeparationLines(separationLinesList);
    }

    setRowsExistent([...rows]);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const citiesResponse = await EntryDataServices.fetchCities();
      setCompleteCities(citiesResponse);

      const { organizedColumns, stateGroupings: stateGroups } = organizeCitiesByState(citiesResponse, initialHeader);
      setCities(organizedColumns);
      setStateGroupings(stateGroups);

      const specialtiesResponse = await EntryDataServices.fetchAttributes(initialHeader);
      setCompleteSpecialties(specialtiesResponse);

      const { hints, separationLines: separationLinesData, fixedFactors } = processSpecialtyData(specialtiesResponse);
      setFixedNetworkAdjustmentFactors(fixedFactors);

      const rows = createInitialRows(specialtiesResponse, organizedColumns.length);

      const existentData = await EntryDataServices.fetchPreExistentData(initialHeader);
      populateRows(rows, organizedColumns, existentData, hints, separationLinesData);

    } catch (err) {
      console.error("Error fetching cities and specialties:", err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [initialHeader, populateRows]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    completeCities,
    completeSpecialties,
    cities,
    stateGroupings,
    rowsExistent,
    fixedNetworkAdjustmentFactors,
    listOfHints,
    separationLines,
    isLoading,
    error,
    refetch: fetchData,
    populateRows
  };
};
